import { Business, HelpOutline } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Checkbox,
  CircularProgress,
  createFilterOptions,
  Fade,
  FilterOptionsState,
  FormControlLabel,
  Grid2,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { CreateOrPutCustomerInput, Customer } from '@thro-types/billing';

import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCustomerQuery, useGetCustomersListQuery } from 'api/hooks/billing/customer';
import { useJobmarketCompanyQuery } from 'api/hooks/jobmarket/companies';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { InfoCustomerCard } from 'app/containers/Billing/components';
import { translations } from 'locales/i18n';
import { countries } from 'utils/Countries';

import { InvoiceFormValues } from '../validation';

interface Props {
  companyId: number;
}

export default function CustomerSearch({ companyId }: Props) {
  const { t } = useTranslation();
  const [filterString, setFilterString] = useState<string>('');
  const { data: customers, isLoading } = useGetCustomersListQuery(true);
  const { values, setFieldValue } = useFormikContext<InvoiceFormValues>();
  const { data: company, isLoading: isLoadingCompany } = useJobmarketCompanyQuery(companyId);
  const currentCountry = countries.find(country => country.label === company?.country);
  const { data: defaultCustomer, isLoading: isDefaultCustomerLoading } = useGetCustomerQuery(
    !!company?.fsvCustomerZPId &&
      !values.customer.useComapnyAsCustomer &&
      !values.customer.customer,
    company?.fsvCustomerZPId || ''
  );
  const currentCustomer: CreateOrPutCustomerInput = {
    customerId: '',
    companyName: company?.sameInvoiceaddress ? company?.name : company?.invoiceName,
    firstName: company?.mainContact.firstName || '',
    lastName: company?.mainContact.lastName || '',
    email: (company?.sameInvoiceaddress ? company?.mainContact.email : company?.invoiceEmail) || '',
    city: (company?.sameInvoiceaddress ? company?.city : company?.invoiceCity) || '',
    country: (company?.sameInvoiceaddress ? currentCountry?.code : company?.invoiceCountry) || '',
    address: (company?.sameInvoiceaddress ? company?.street : company?.invoiceStreet) || '',
    zip: (company?.sameInvoiceaddress ? company?.zip : company?.invoiceZip) || '',
    phone: company?.phone || ''
  };

  useEffect(() => {
    if (defaultCustomer && !values.customer.customer) {
      setFieldValue('customer.customer', defaultCustomer);
      setFieldValue('customer.useCompanyAsCustomer', false);
    }
  }, [defaultCustomer]);

  const filterOptions = <T extends Object>(options: T[], state: FilterOptionsState<T>) => {
    const OPTIONS_LIMIT = filterString.length < 3 ? 20 : undefined;
    const defaultFilterOptions = createFilterOptions<T>({
      stringify: option => Object.values(option).join(' ')
    });
    return defaultFilterOptions(options, state).slice(0, OPTIONS_LIMIT);
  };

  const getOptionLabel = (
    param: React.HTMLAttributes<HTMLLIElement> & {
      key: any;
    },
    customer: Customer
  ) => {
    const { key, ...rest } = param;
    if (customer.companyName && customer.ustId) {
      return (
        <MenuItem {...rest} key={key}>
          <Business sx={{ mr: 1 }} />
          {customer.companyName}
        </MenuItem>
      );
    }
  };
  if (isLoading || isLoadingCompany || isDefaultCustomerLoading) {
    return <CircularProgress />;
  }

  return (
    <Grid2 container spacing={4} justifyContent={'flex-start'} flexDirection={'row'}>
      <ErrorBoundary boundaryTag="customerSearchBoundary">
        <Grid2
          container
          gap={2}
          justifyContent={'flex-start'}
          flexDirection={'column'}
          size={{ xs: 12, lg: 12, xl: 4 }}
        >
          <Grid2 container flexDirection={'row'} gap={1} alignItems={'center'} display={'flex'}>
            <Typography variant="h6">{t(translations.common.customer())}</Typography>
            <Tooltip title={t(translations.pages.invoices.noCustomerInfoText())}>
              <HelpOutline fontSize="medium" style={{ opacity: 0.5 }} />
            </Tooltip>
          </Grid2>
          <Autocomplete
            filterOptions={filterOptions}
            options={customers?.data || []}
            loading={isLoading}
            renderOption={getOptionLabel}
            getOptionLabel={customer =>
              customer.companyName
                ? customer.companyName
                : `${customer.firstName} ${customer.lastName}`
            }
            getOptionKey={customer => customer.id}
            renderInput={params => <TextField {...params} />}
            size="small"
            onInputChange={(event, value) => setFilterString(value)}
            onChange={(event, value) => {
              setFieldValue('customer.customer', value);
              if (value) {
                setFieldValue('customer.useCompanyAsCustomer', false);
              } else {
                setFieldValue('customer.useCompanyAsCustomer', true);
              }
            }}
            noOptionsText={
              filterString
                ? t(translations.pages.billing.noOptions())
                : t(translations.pages.billing.typeToSearch())
            }
          />
          <TextField
            size="small"
            disabled
            value={
              values?.customer?.customer?.companyName ||
              `${values?.customer?.customer?.firstName || ''} ${
                values?.customer?.customer?.lastName || ''
              }`
            }
            slotProps={{
              input: { startAdornment: <Business /> }
            }}
          />
          <FormControlLabel
            value={values.customer.useCompanyAsCustomer}
            control={
              <Checkbox
                size="small"
                checked={values.customer.useCompanyAsCustomer}
                onChange={e => {
                  setFieldValue('customer.useCompanyAsCustomer', e.target.checked);
                }}
              />
            }
            label={
              <Typography variant="body2">
                {t(translations.pages.invoices.noCustomerFound())}
              </Typography>
            }
            labelPlacement="end"
          />
        </Grid2>
        <Grid2 container spacing={2} size={{ xs: 12, xl: 8 }}>
          <Grid2
            container
            justifyContent={'flex-start'}
            flexDirection={'column'}
            gap={1}
            size={{ xs: 12, lg: 6 }}
          >
            <Typography variant="h6" paddingBottom={1}>
              {t(translations.pages.invoices.jobmarketCustomer())}
            </Typography>
            <Grid2
              onClick={() => {
                setFieldValue('customer.useCompanyAsCustomer', true);
              }}
              sx={
                values.customer.useCompanyAsCustomer && {
                  border: '1px solid lightgrey'
                }
              }
            >
              <InfoCustomerCard data={currentCustomer} hideEdit showEmptyPhone isClickable={true} />
            </Grid2>
            <Fade in={values.customer.useCompanyAsCustomer}>
              <Alert severity="info">{'Diese Daten werden verwendet'}</Alert>
            </Fade>
          </Grid2>
          <Grid2
            container
            justifyContent={'flex-start'}
            flexDirection={'column'}
            size={{ xs: 12, lg: 6 }}
            gap={1}
          >
            <Typography variant="h6" paddingBottom={1}>
              {t(translations.pages.invoices.customerInFSV())}
            </Typography>
            {values.customer.customer ? (
              <Grid2
                onClick={() => {
                  setFieldValue('customer.useCompanyAsCustomer', false);
                }}
                sx={{
                  ...(!values.customer.useCompanyAsCustomer && {
                    border: '1px solid lightgrey'
                  })
                }}
              >
                <InfoCustomerCard
                  data={{ ...values.customer.customer }}
                  hideEdit
                  showEmptyPhone
                  isClickable={true}
                />
              </Grid2>
            ) : (
              <Grid2
                container
                component={Paper}
                style={{ height: '281px', width: '100%' }}
                justifyContent={'center'}
                alignContent={'center'}
              >
                <Typography variant="h6" paddingBottom={1}>
                  {t(translations.pages.invoices.noCustomerSelected())}
                </Typography>
              </Grid2>
            )}
            <Fade in={!values.customer.useCompanyAsCustomer}>
              <Alert severity="info">{'Diese Daten werden verwendet'}</Alert>
            </Fade>
          </Grid2>
        </Grid2>
      </ErrorBoundary>
    </Grid2>
  );
}
